import { useState } from 'react';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { Crown, SkipForward, X, Check } from 'lucide-react';

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  voteCount: number;
  isCurrentPlayer: boolean;
}

interface MVPVotingModalProps {
  isOpen: boolean;
  players: Player[];
  onClose: () => void;
  onVote: (targetSeat: number | null) => void; // null 表示跳过
}

export default function MVPVotingModal({
  isOpen,
  players,
  onClose,
  onVote,
}: MVPVotingModalProps) {
  const { theme } = useTheme();
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);
  const [hasVoted, setHasVoted] = useState(false);

  if (!isOpen) return null;

  const handleVote = () => {
    setHasVoted(true);
    onVote(selectedSeat);
  };

  const handleSkip = () => {
    setHasVoted(true);
    onVote(null);
  };

  // 按票数排序
  const sortedPlayers = [...players].sort((a, b) => b.voteCount - a.voteCount);
  const maxVotes = sortedPlayers[0]?.voteCount || 0;
  const topPlayers = sortedPlayers.filter((p) => p.voteCount === maxVotes && maxVotes > 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: theme.colors['accent-gold'] }}
          >
            <Crown size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: theme.colors['accent-gold'] }}
            >
              MVP投票
            </h2>
          </div>
        </div>

        {/* 说明 */}
        <div
          className="mb-6 p-3 rounded-lg text-center text-sm"
          style={{
            backgroundColor: `${theme.colors['accent-gold']}10`,
            border: `1px solid ${theme.colors['accent-gold']}30`,
            color: theme.colors['text-secondary'],
          }}
        >
          请选择你认为本局表现最佳的玩家
          <br />
          平票时将进行二次投票
        </div>

        {/* 投票进度 */}
        <div
          className="mb-4 p-3 rounded-lg flex items-center justify-between"
          style={{
            backgroundColor: theme.colors['bg-primary'],
          }}
        >
          <span
            className="text-sm"
            style={{ color: theme.colors['text-secondary'] }}
          >
            投票进度
          </span>
          <span
            className="text-sm font-medium"
            style={{ color: theme.colors['accent-gold'] }}
          >
            {players.filter((p) => p.voteCount > 0).length}/{players.length} 人已投
          </span>
        </div>

        {/* 玩家列表 */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {sortedPlayers.map((player) => (
            <button
              key={player.id}
              onClick={() => !hasVoted && setSelectedSeat(player.seatNumber)}
              disabled={hasVoted}
              className="relative flex flex-col items-center p-3 rounded-xl transition-all"
              style={{
                backgroundColor:
                  selectedSeat === player.seatNumber
                    ? `${theme.colors['accent-gold']}30`
                    : player.voteCount > 0
                    ? `${theme.colors['accent-gold']}10`
                    : theme.colors['bg-primary'],
                border: `2px solid ${
                  selectedSeat === player.seatNumber
                    ? theme.colors['accent-gold']
                    : player.voteCount > 0
                    ? `${theme.colors['accent-gold']}50`
                    : `${theme.colors['border-secondary']}40`
                }`,
                opacity: hasVoted ? 0.7 : 1,
              }}
            >
              {/* 头像 */}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-2"
                style={{
                  backgroundColor: theme.colors['accent-purple'],
                  color: theme.colors['bg-primary'],
                }}
              >
                {player.nickname[0]}
              </div>
              {/* 昵称 */}
              <span
                className="text-xs font-medium truncate w-full text-center"
                style={{ color: theme.colors['text-primary'] }}
              >
                {player.nickname}
              </span>
              {/* 座位号 */}
              <span
                className="text-xs"
                style={{ color: theme.colors['text-muted'] }}
              >
                #{player.seatNumber}
              </span>

              {/* 票数显示 */}
              {player.voteCount > 0 && (
                <div
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: theme.colors['accent-gold'],
                    color: theme.colors['bg-primary'],
                  }}
                >
                  {player.voteCount}
                </div>
              )}

              {/* 已选择标记 */}
              {selectedSeat === player.seatNumber && !hasVoted && (
                <div
                  className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: theme.colors['accent-gold'],
                    color: theme.colors['bg-primary'],
                  }}
                >
                  已选择
                </div>
              )}
            </button>
          ))}
        </div>

        {/* 平票提示 */}
        {topPlayers.length > 1 && maxVotes > 0 && (
          <div
            className="mb-4 p-3 rounded-lg"
            style={{
              backgroundColor: `${theme.colors['accent-gold']}15`,
              border: `1px solid ${theme.colors['accent-gold']}40`,
            }}
          >
            <p
              className="text-sm text-center font-medium mb-2"
              style={{ color: theme.colors['accent-gold'] }}
            >
              平票！请在以下玩家中进行二次投票
            </p>
            <div className="flex justify-center gap-2">
              {topPlayers.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: theme.colors['accent-gold'],
                    color: theme.colors['bg-primary'],
                  }}
                >
                  #{p.seatNumber} {p.nickname}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        {!hasVoted && (
          <div className="flex gap-3">
            <ThemedButton
              variant="secondary"
              onClick={handleSkip}
              className="flex-1"
            >
              <SkipForward size={18} className="mr-1" />
              跳过
            </ThemedButton>
            <ThemedButton
              onClick={handleVote}
              disabled={selectedSeat === null}
              className="flex-1"
            >
              <Check size={18} className="mr-1" />
              确认投票
            </ThemedButton>
          </div>
        )}

        {/* 等待提示 */}
        {hasVoted && (
          <div
            className="text-center p-4 rounded-lg"
            style={{
              backgroundColor: theme.colors['bg-primary'],
              color: theme.colors['text-secondary'],
            }}
          >
            <p>投票已提交，等待其他玩家...</p>
          </div>
        )}
      </ThemedCard>
    </div>
  );
}
